/** /changes — cross-object change history grid (objchange rows).
 *
 *  The batch-wide view of what `HistoryPanel` shows per object: filter by
 *  task_id (everything one background task changed — Audit Log and Background
 *  Tasks deep-link here), content type, action or source, then mass-restore a
 *  selection ("roll back this batch"). Clicking a row opens the record's full
 *  history panel — before/after values and the per-row restore live there.
 */
import { Box, Chip, Dialog, DialogContent, DialogTitle, Link, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as changesApi from '@/api/changes';
import type { ChangeAction, ObjectChange } from '@/api/changes';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { auditHref, changesHref, consoleRouteFor } from '@/utils/gridLinks';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import type { BatchActionDef } from '@/components/DataGrid/batchTypes';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { BatchRestoreDialog } from './BatchRestoreDialog';

const passthrough = (v: string) => v || undefined;

const FILTERS: FilterDef[] = [
  { param: 'task_id', toApi: passthrough },
  { param: 'content_type', toApi: passthrough },
  { param: 'action', toApi: passthrough },
  { param: 'source', toApi: passthrough },
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
];

const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

/** The changed record: its console page when the content type has one (the
 *  shared map), copy of the pk either way. */
function recordActions(row: ObjectChange): ValueAction[] {
  const to = consoleRouteFor(row.content_type, row.object_pk);
  return [
    ...(to ? [act.open(to, 'Open record')] : []),
    act.copy(row.object_pk, 'Copy record pk'),
    act.filter(changesHref({ content_type: row.content_type }), 'Changes to this record type'),
  ];
}

function taskActions(taskId: string): ValueAction[] {
  return [
    act.copy(taskId, 'Copy task id'),
    act.open(`/bgtasks/${taskId}`, 'Open the background task'),
    act.filter(changesHref({ task_id: taskId }), 'Every change by this task'),
    act.filter(auditHref({ task_id: taskId }), 'Audit trail of this task'),
  ];
}

const ACTION_COLOR: Record<ChangeAction, 'success' | 'info' | 'error' | 'warning'> = {
  created: 'success',
  updated: 'info',
  deleted: 'error',
  restored: 'warning',
};

const SOURCES = [
  'console',
  'dashboard',
  'admin',
  'api',
  'integration',
  'management',
  'celery',
  'signal',
  'system',
  // Change sets (A3c): MCP / console-agent applies, translation syncs, reverts.
  'mcp',
  'chat',
  'translation_sync',
  'revert',
];

const DEFAULT_ORDER = [
  'id',
  'created_ts',
  'content_type',
  'object_repr',
  'action',
  'actor',
  'source',
  'changed_paths',
  'comment',
  'task_id',
];

const DEFAULT_HIDDEN = ['id', 'task_id'];

const restoreBatchAction: BatchActionDef = {
  key: 'restore',
  label: 'Restore previous values…',
  action: 'restore',
  permission: 'console_changes_restore',
  dialog: (ctx) => <BatchRestoreDialog ctx={ctx} />,
};

export function ChangesListPage() {
  const [detailRow, setDetailRow] = useState<ObjectChange | null>(null);

  const presets = useGridPresets({
    grid: 'changes',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  const taskId = getParam('task_id') ?? '';
  const contentType = getParam('content_type') ?? '';
  const action = getParam('action') ?? 'all';
  const source = getParam('source') ?? 'all';
  // The operators the effective tenant filter names (URL > preset > scope).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());

  const columns = useMemo<GridColDef<ObjectChange>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 90 }),
      {
        field: 'created_ts',
        headerName: 'When',
        width: 170,
        valueGetter: (_, row) => row.created_ts,
        renderCell: (p) => new Date(p.row.created_ts).toLocaleString(),
      },
      {
        field: 'content_type',
        headerName: 'Type',
        width: 180,
        sortable: false,
        renderCell: (p) => (
          <ValueActions
            chip
            actions={[act.filter(changesHref({ content_type: p.row.content_type }), 'Changes of this type')]}
          >
            <Chip size="small" variant="outlined" label={p.row.content_type} />
          </ValueActions>
        ),
      },
      {
        field: 'object_repr',
        headerName: 'Record',
        flex: 1,
        minWidth: 200,
        sortable: false,
        valueGetter: (_, row) => row.object_repr || row.object_pk,
        renderCell: (p) => {
          const to = consoleRouteFor(p.row.content_type, p.row.object_pk);
          const text = p.row.object_repr || p.row.object_pk;
          return (
            <ValueActions actions={recordActions(p.row)}>
              {to ? (
                // The row click opens the history dialog; the link navigates instead.
                <Link component={RouterLink} to={to} underline="hover" onClick={(e) => e.stopPropagation()}>
                  {text}
                </Link>
              ) : (
                text
              )}
            </ValueActions>
          );
        },
      },
      {
        field: 'action',
        headerName: 'Action',
        width: 110,
        renderCell: (p) => (
          <ValueActions chip actions={[act.filter(changesHref({ action: p.row.action }), `Only "${p.row.action}" changes`)]}>
            <Chip size="small" color={ACTION_COLOR[p.row.action]} label={p.row.action} />
          </ValueActions>
        ),
      },
      {
        field: 'actor',
        headerName: 'By',
        width: 200,
        sortable: false,
        valueGetter: (_, row) => row.actor?.email ?? '',
        renderCell: (p) =>
          p.row.actor ? (
            <ValueActions
              actions={[
                ...(p.row.actor.id ? [act.open(`/users/${p.row.actor.id}`, 'Open user')] : []),
                act.copy(p.row.actor.email, 'Copy email'),
                act.filter(auditHref({ actor_email: p.row.actor.email }), 'Audit entries by this user'),
              ]}
            >
              {p.row.actor.email}
            </ValueActions>
          ) : (
            '—'
          ),
      },
      {
        field: 'source',
        headerName: 'Via',
        width: 110,
        renderCell: (p) => (
          <ValueActions actions={[act.filter(changesHref({ source: p.row.source }), `Changes via ${p.row.source}`)]}>
            {p.row.source}
          </ValueActions>
        ),
      },
      {
        field: 'changed_paths',
        headerName: 'Changed',
        flex: 1,
        minWidth: 160,
        sortable: false,
        valueGetter: (_, row) => row.changed_paths.join(', '),
        renderCell: (p) => (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {p.row.changed_paths.slice(0, 5).map((path) => (
              <Chip key={path} size="small" variant="outlined" label={path} />
            ))}
            {p.row.changed_paths.length > 5 && (
              <Chip size="small" label={`+${p.row.changed_paths.length - 5}`} />
            )}
          </Box>
        ),
      },
      {
        field: 'comment',
        headerName: 'Reason',
        flex: 1,
        minWidth: 160,
        sortable: false,
        valueGetter: (_, row) => row.comment,
      },
      {
        field: 'task_id',
        headerName: 'Task',
        width: 140,
        renderCell: (p) =>
          p.row.task_id ? (
            <ValueActions actions={taskActions(p.row.task_id)}>
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.row.task_id}</Box>
            </ValueActions>
          ) : (
            '—'
          ),
      },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      changesApi.list({
        limit: params.pageSize,
        offset: params.page * params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? '-created_ts',
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'task_id',
      priority: 6,
      active: taskId !== '',
      node: (
        <TextField
          size="small"
          label="Task ID"
          value={taskId}
          sx={{ width: 300 }}
          placeholder="Batch task UUID"
          onChange={(e) => setParam('task_id', e.target.value.trim() || null)}
        />
      ),
    },
    {
      key: 'content_type',
      priority: 5,
      active: contentType !== '',
      node: (
        <TextField
          size="small"
          label="Type"
          value={contentType}
          sx={{ width: 200 }}
          placeholder="demo.launch_policy"
          onChange={(e) => setParam('content_type', e.target.value.trim() || null)}
        />
      ),
    },
    {
      key: 'action',
      priority: 4,
      active: action !== 'all',
      node: (
        <TextField
          select
          size="small"
          label="Action"
          value={action}
          sx={{ width: 140 }}
          onChange={(e) => setParam('action', e.target.value === 'all' ? null : e.target.value)}
        >
          <MenuItem value="all">Any action</MenuItem>
          {(['created', 'updated', 'deleted', 'restored'] as ChangeAction[]).map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'source',
      priority: 3,
      active: source !== 'all',
      node: (
        <TextField
          select
          size="small"
          label="Via"
          value={source}
          sx={{ width: 150 }}
          onChange={(e) => setParam('source', e.target.value === 'all' ? null : e.target.value)}
        >
          <MenuItem value="all">Any source</MenuItem>
          {SOURCES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'tenant',
      priority: 2,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="ui-change-history"
        title="Changes"
        subtitle="Field-level change history across every tracked record. Filter by task ID to see everything one batch did — and roll it back."
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <ResourceDataGrid<ObjectChange>
        queryKey={['changes']}
        queryFn={queryFn}
        columns={orderColumns(columns, presets.orderedFields)}
        extra={extra}
        batchResource="changes"
        batchActions={[restoreBatchAction]}
        batchRowTenantId={(row) => row.tour_operator_id}
        defaultSort={{ field: 'created_ts', sort: 'desc' }}
        sortModel={presets.sortModel}
        onSortModelChange={presets.onSortModelChange}
        columnVisibilityModel={presets.columnVisibilityModel}
        onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
        onColumnOrderChange={presets.onColumnOrderChange}
        onRowClick={(_, row) => setDetailRow(row)}
        emptyMessage="No recorded changes match these filters."
        headerToolbar={{
          onResetAllFilters: resetAllFilters,
          resetDisabled: !anyFilterActive,
          search: false,
          exportFileName: 'changes',
          presets: presets.toolbar,
        }}
      />

      {/* Row click → the record's full history (before/after + per-row restore). */}
      <Dialog open={detailRow != null} onClose={() => setDetailRow(null)} fullWidth maxWidth="lg">
        {detailRow && (
          <>
            <DialogTitle>
              <ValueActions actions={recordActions(detailRow)}>
                {detailRow.object_repr || `${detailRow.content_type} #${detailRow.object_pk}`}
              </ValueActions>{' '}
              — change history
            </DialogTitle>
            <DialogContent>
              <HistoryPanel
                contentType={detailRow.content_type}
                objectPk={detailRow.object_pk}
                aggregate={false}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
